import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDashboard } from '../../context/DashboardContext';
import {
  adminCacheKey,
  approveGoalSubmission,
  approveSubmission,
  loadAdminRoster,
  loadApprovedSubmissions,
  loadPendingApprovals,
  peekAdminCache,
  putAdminCache,
} from '../../api/approvalsApi';
import { notifyGoalsUpdated } from '../../utils/goalsLocalStore';
import ApprovalTable from './ApprovalTable';
import ApprovalSheet from './ApprovalSheet';
import GoalApprovalSheet from './GoalApprovalSheet';
import StaffTable from './StaffTable';
import '../../styles/adminApproval.css';

const TABS = [
  { key: 'roster', label: 'スタイリスト一覧' },
  { key: 'done', label: '行動記録管理' },
  { key: 'pending', label: '確認中' },
];

/**
 * 管理画面 — 3 subtabs (スタイリスト一覧 → 行動記録管理 → 確認中).
 * `canApprove` false = same screens, read-only, no 確定 (every 管理画面 role can approve today).
 * Store scope is enforced by the server; the store select only narrows the view.
 */
export default function AdminScreen({ canApprove, canApproveGoals = canApprove, onPendingCount }) {
  const { selectedStore, stores, selectedMonth } = useDashboard();
  // 店舗タブの人数ブロックから来たときは `shop`。ホームの store / stylist は書き換えず、この画面だけで使う。
  const shopParam = useSearchParams()[0].get('shop') || '';
  const [store, setStore] = useState(shopParam || selectedStore || '');
  const [inner, setInner] = useState('roster');
  // Start from what was prefetched at login (SalonDashboard) — no spinner on open;
  // the loaders below refresh it in the background.
  const [pending, setPending] = useState(() => peekAdminCache(adminCacheKey.pending())?.submissions || []);
  const [done, setDone] = useState(() => peekAdminCache(adminCacheKey.approved())?.submissions || []);
  const [rosterRows, setRosterRows] = useState(
    () => peekAdminCache(adminCacheKey.roster(shopParam || selectedStore, selectedMonth))?.stylists || []
  );
  const [loading, setLoading] = useState({ queue: false, roster: false });
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!store && selectedStore) setStore(selectedStore);
  }, [selectedStore, store]);

  // 画面を開いたまま `shop` が変わったとき（店舗タブからもう一度来たとき）
  useEffect(() => {
    if (shopParam) setStore(shopParam);
  }, [shopParam]);

  const loadQueue = useCallback(async () => {
    setLoading((l) => ({ ...l, queue: true }));
    setError(null);
    try {
      // Queue + history cover every store in scope (the tab badge counts all of them);
      // the store select only narrows スタイリスト一覧.
      const [p, d] = await Promise.all([loadPendingApprovals(), loadApprovedSubmissions()]);
      setPending(p.submissions || []);
      setDone(d.submissions || []);
      onPendingCount?.((p.submissions || []).length);
    } catch (err) {
      setError(err.message || '読み込みに失敗しました');
    } finally {
      setLoading((l) => ({ ...l, queue: false }));
    }
  }, [onPendingCount]);

  const loadRoster = useCallback(async () => {
    if (!store || !selectedMonth) return;
    const cached = peekAdminCache(adminCacheKey.roster(store, selectedMonth));
    if (cached) setRosterRows(cached.stylists || []);
    setLoading((l) => ({ ...l, roster: true }));
    try {
      const r = await loadAdminRoster(store, selectedMonth);
      setRosterRows(r.stylists || []);
    } catch (err) {
      setError(err.message || '一覧の読み込みに失敗しました');
    } finally {
      setLoading((l) => ({ ...l, roster: false }));
    }
  }, [store, selectedMonth]);

  useEffect(() => { void loadQueue(); }, [loadQueue]);
  useEffect(() => { void loadRoster(); }, [loadRoster]);

  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(''), 2400);
  };

  // 行動 (actions) and 目標 (goals) submissions share the queue; only the request differs.
  const onApprove = async (payload, note) => {
    if (!open || submitting) return;
    setSubmitting(true);
    try {
      const base = { submissionId: open.id, store: open.store, stylist: open.stylist, managerNote: note };
      const res = open.type === 'goals'
        ? await approveGoalSubmission({ ...base, patch: payload })
        : await approveSubmission({ ...base, actions: payload });
      const moved = res.submission || { ...open, status: 'approved' };
      const nextPending = pending.filter((x) => x.id !== open.id);
      const nextDone = [moved, ...done.filter((x) => x.id !== moved.id)];
      setPending(nextPending);
      setDone(nextDone);
      putAdminCache(adminCacheKey.pending(), { submissions: nextPending, count: nextPending.length });
      putAdminCache(adminCacheKey.approved(), { submissions: nextDone, count: nextDone.length });
      onPendingCount?.(nextPending.length);
      if (res.goals) notifyGoalsUpdated(res.goals);
      setOpen(null);
      setInner('done');
      setFlashId(moved.id);
      setTimeout(() => setFlashId(null), 2400);
      showToast(
        open.type === 'goals'
          ? `${open.stylist} の目標を確定しました。本人に通知します。`
          : note ? `${open.stylist} を確定。コメントはスタイリストのタブとAIへ。` : `${open.stylist} を確定しました。`
      );
      void loadRoster();
    } catch (err) {
      if (err.status === 409) {
        setOpen(null);
        showToast('この提出は既に処理済みです。一覧を更新しました。');
        void loadQueue();
      } else {
        showToast(err.message || '確定に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isPendingRow = open ? pending.some((x) => x.id === open.id) : false;

  return (
    <div className="adm-screen">
      {inner === 'roster' && stores && stores.length > 1 && (
        <select className="adm-store" value={store} onChange={(e) => setStore(e.target.value)} aria-label="店舗">
          {stores.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      )}
      <div className="adm-pills">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`adm-pill${inner === t.key ? ' is-on' : ''}`}
            onClick={() => { setInner(t.key); setOpen(null); }}
          >
            {t.label}
            {t.key === 'pending' && pending.length > 0 && <span className="adm-bang">!</span>}
          </button>
        ))}
      </div>

      {error && <div className="adm-error" role="alert">{error}</div>}

      {inner === 'roster' && (
        <StaffTable rows={rosterRows} loading={loading.roster} />
      )}
      {inner === 'done' && (
        <ApprovalTable rows={done} status="確定" flashId={flashId} onOpen={setOpen} />
      )}
      {inner === 'pending' && (
        loading.queue && pending.length === 0
          ? <div className="apv-empty">読み込み中…</div>
          : <ApprovalTable rows={pending} status="確認中" onOpen={setOpen} />
      )}

      {open && open.type === 'goals' && (
        <GoalApprovalSheet
          key={open.id}
          row={open}
          isPending={isPendingRow}
          canEdit={canApproveGoals}
          submitting={submitting}
          onClose={() => setOpen(null)}
          onApprove={onApprove}
        />
      )}
      {open && open.type !== 'goals' && (
        <ApprovalSheet
          key={open.id}
          row={open}
          isPending={isPendingRow}
          canEdit={canApprove}
          submitting={submitting}
          onClose={() => setOpen(null)}
          onApprove={onApprove}
        />
      )}
      {toast && <div className="adm-toast">{toast}</div>}
    </div>
  );
}
